import { notifySessionExpired, requireMfaStepUp } from "../auth/mfaStepUp";

export const SETUP_STEPS = ["signer", "policy", "keys", "root", "activation"] as const;
export type SetupStep = typeof SETUP_STEPS[number];
export type PublishingSetupPlan = {
  contract_version: "kavoosh-collector-publishing-setup.v1";
  revision: string;
  state: "ready" | "action_required" | "blocked";
  code: string | null;
  steps: Record<SetupStep, boolean>;
  next_step: SetupStep | null;
  private_material_exposed: false;
};

async function request(revision?: string, retry = true): Promise<PublishingSetupPlan> {
  const csrf = document.cookie.split(";").map((item) => item.trim())
    .find((item) => item.startsWith("kavoosh_csrf="))?.slice("kavoosh_csrf=".length);
  const response = await fetch(`/api/v1/admin/collector-publishing/setup${revision ? "/advance" : ""}`, {
    method: revision ? "POST" : "GET",
    credentials: "include", cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(revision ? { "Content-Type": "application/json" } : {}),
      ...(revision && csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {}),
    },
    ...(revision ? { body: JSON.stringify({ expected_revision: revision }) } : {}),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = typeof body.detail === "object" ? body.detail?.code : null;
    if (retry && ["MFA_STEP_UP_REQUIRED", "AUTH_MFA_REQUIRED"].includes(code)) {
      await requireMfaStepUp();
      return request(revision, false);
    }
    if (response.status === 401) notifySessionExpired();
    throw new Error(code ?? (response.status === 503
      ? "publishing_setup_configuration" : "publishing_setup_verification_failed"));
  }
  if (body.contract_version !== "kavoosh-collector-publishing-setup.v1"
      || !/^[0-9a-f]{64}$/.test(body.revision)
      || !["ready", "action_required", "blocked"].includes(body.state)
      || (body.code !== null && typeof body.code !== "string")
      || !SETUP_STEPS.every((step) => typeof body.steps?.[step] === "boolean")
      || (body.next_step !== null && !SETUP_STEPS.includes(body.next_step))
      || body.private_material_exposed !== false) {
    throw new Error("publishing_setup_verification_failed");
  }
  const next = SETUP_STEPS.find((step) => !body.steps[step]) ?? null;
  if ((body.state === "ready" && (next !== null || body.next_step !== null))
      || (body.state === "action_required" && (next === null || body.next_step !== next))
      || (body.state === "blocked" && body.next_step !== null)) {
    throw new Error("publishing_setup_verification_failed");
  }
  return body as PublishingSetupPlan;
}

export const getPublishingSetup = () => request();
export const advancePublishingSetup = (revision: string) => request(revision);
